import { useCallback, useState, useMemo, useEffect } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import ROUTES from 'app/routes'
import { useAppSelector } from 'app/hooks'
import { makePublicUrl } from 'app/utils/publicUrl'
import { Dropdown } from 'components/Input'
import { DropdownOption } from 'components/Input/Dropdown'
import {
  useNetworkHomeJobsRouteParams,
  useCurrentHomeSiteId,
  useNetworkOrgId,
} from 'features/networkAi/hooks'
import {
  subscribedAccessPointsSelector,
  claimDevicesFetchDataSelector,
  mistSiteFetchDataSelector,
} from 'features/networkAi/selectors'
import {
  fetchClaimDevicesThunk,
  fetchInventoryThunk,
} from 'features/networkAi/slice'
import { network } from 'features/networkAi/messages'
import DataCard from './DataCard'
import SubscriptionStatusCard from './SubscriptionStatusCard'
import WLANsCard from './WLANsCard'
import ClientsCard from './ClientsCard'
import SubsriptionStatusInformation from './SubsriptionStatusInformation'

import {
  NetworkHomeWidgetContainer,
  NetworkHomeWidgetHeader,
  NetworkHomeWidgetTitle,
  NavigateToNetworkAIIcon,
  NetworkAINavigateButton,
  NetworkAITitleIcon,
  NetworkHomeWidgetInputMaster,
  NetworkHomeWidgetInputBorder,
  NetworkHomeWidgetHealthContainer,
  NetworkHomeWidgetSubsContainer,
  NetworkHomeWidgetLink,
} from './styles'

const GRAPH_RANGE_OPTIONS = [
  { value: 43200, label: 'Today' },
  { value: 86400, label: '24 Hours' },
  { value: 604800, label: 'Last 7 Days' },
]

const GraphRangeOptions = GRAPH_RANGE_OPTIONS.map((option) => ({
  label: option.label,
  value: option.value,
}))

function NetworkHomeWidget() {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const organizationId = useNetworkOrgId()
  const { rootDomainId, homeId, jobId } = useNetworkHomeJobsRouteParams()
  const siteId = useCurrentHomeSiteId({ homeId })
  const claimDevices = useAppSelector(claimDevicesFetchDataSelector.data)
  const subscribedAccessPoints = useAppSelector(subscribedAccessPointsSelector)
  const [duration, setDuration] = useState(604800)
  const isLoading = useAppSelector(mistSiteFetchDataSelector.isFetching)

  useEffect(() => {
    if (organizationId) dispatch(fetchInventoryThunk(siteId))
    if (siteId) dispatch(fetchClaimDevicesThunk(siteId))
  }, [dispatch, siteId, organizationId])

  const valueDuration = useMemo(
    () =>
      GraphRangeOptions.find((option) => option.value === duration) || {
        label: `${duration} `,
        value: duration,
      },
    [duration]
  )

  const handleNavigateNetworkAIClick = useCallback(() => {
    const path = generatePath(ROUTES.networkAi.dashboard.rootNoJob, {
      rootDomainId,
      jobId,
      homeId,
    })
    history.push(path)
  }, [history, homeId, jobId, rootDomainId])

  return (
    <>
      {!isLoading &&
        siteId !== '' &&
        subscribedAccessPoints.count !== undefined &&
        subscribedAccessPoints.count > 0 && (
          <NetworkHomeWidgetContainer>
            <NetworkHomeWidgetHeader>
              <NetworkAITitleIcon
                src={makePublicUrl(`images/networkIcons/router.svg`)}
              />
              <NetworkHomeWidgetTitle>
                {formatMessage(network.title)}
              </NetworkHomeWidgetTitle>

              {subscribedAccessPoints.count !== 0 && (
                <NetworkHomeWidgetInputMaster>
                  <NetworkHomeWidgetInputBorder>
                    <Dropdown
                      options={GraphRangeOptions}
                      name="graphRange"
                      value={valueDuration}
                      placeholder=""
                      onChange={(option: DropdownOption) =>
                        setDuration(Number(option.value))
                      }
                    />
                  </NetworkHomeWidgetInputBorder>
                </NetworkHomeWidgetInputMaster>
              )}

              <NetworkHomeWidgetLink>
                <NetworkAINavigateButton onClick={handleNavigateNetworkAIClick}>
                  <NavigateToNetworkAIIcon
                    src={makePublicUrl(`images/networkIcons/arrow-forward.svg`)}
                  />
                </NetworkAINavigateButton>
              </NetworkHomeWidgetLink>
            </NetworkHomeWidgetHeader>

            {subscribedAccessPoints.count !== 0 && (
              <NetworkHomeWidgetHealthContainer>
                <DataCard
                  title="Time to Connect"
                  siteId={siteId}
                  metric="time-to-connect"
                  duration={duration}
                ></DataCard>
                <DataCard
                  title="Successful Connects"
                  siteId={siteId}
                  metric="failed-to-connect"
                  duration={duration}
                ></DataCard>
                <DataCard
                  title="Coverage"
                  siteId={siteId}
                  metric="coverage"
                  duration={duration}
                ></DataCard>
                <DataCard
                  title="Roaming"
                  siteId={siteId}
                  metric="roaming"
                  duration={duration}
                ></DataCard>
                <DataCard
                  title="Throughput"
                  siteId={siteId}
                  metric="throughput"
                  duration={duration}
                ></DataCard>
                <DataCard
                  title="Capacity"
                  siteId={siteId}
                  metric="capacity"
                  duration={duration}
                ></DataCard>
                <DataCard
                  title="AP Health"
                  siteId={siteId}
                  metric="ap-health"
                  duration={duration}
                ></DataCard>
              </NetworkHomeWidgetHealthContainer>
            )}

            <NetworkHomeWidgetSubsContainer>
              <WLANsCard siteId={siteId} />
              <ClientsCard siteId={siteId} />
              <SubscriptionStatusCard
                subscribedCount={subscribedAccessPoints.count}
                claimedCount={claimDevices?.length || 0}
              />
            </NetworkHomeWidgetSubsContainer>

            {(claimDevices?.length || 0) > subscribedAccessPoints.count && (
              <SubsriptionStatusInformation
                rootDomainId={rootDomainId}
                jobId={jobId}
                homeId={homeId}
              />
            )}
          </NetworkHomeWidgetContainer>
        )}
    </>
  )
}

export default NetworkHomeWidget
